import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AppRoute, AppState, MasteryEvidenceType, Skill, ThemeMode, WordItem } from '../types';
import { answerSkill as applySkillAnswer, answerWord, completeLesson, getEntitlement } from './learning';
import { defaultState, loadState, saveState } from './storage';
import { pullCloudState, pushCloudState, refreshLicenseHubStatus } from './cloud';
import { initTelegram, telegramTheme, telegramUser } from './telegram';
import { queueLearningEvent } from './analytics';
import { ensureOfflinePack } from './offline';
import { verifyCachedNativeLicense } from './license-device';
import { getSession } from './supabase';
import { applyCalendarSnapshot } from './calendar';

interface PaidAccessRefreshResult {
  ok: boolean;
  active: boolean;
  reason: string;
  expiresAt?: string;
}

interface AppContextValue {
  state: AppState;
  ready: boolean;
  authenticated: boolean;
  route: AppRoute;
  setRoute: (route: AppRoute) => void;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  updateProfile: (patch: Partial<AppState['profile']>) => void;
  updateSettings: (patch: Partial<AppState['settings']>) => void;
  answer: (word: WordItem, correct: boolean, latencyMs?: number, evidenceType?: MasteryEvidenceType) => void;
  answerSkill: (skill: Exclude<Skill, 'vocabulary'>, correct: boolean, key?: string) => void;
  finishLesson: (score: number) => void;
  theme: 'light' | 'dark';
  setThemeMode: (mode: ThemeMode) => void;
  syncNow: () => Promise<boolean>;
  refreshEntitlement: (force?: boolean) => Promise<PaidAccessRefreshResult>;
  entitlement: ReturnType<typeof getEntitlement>;
}

const AppContext = createContext<AppContextValue | null>(null);
const appViews: AppRoute[] = ['dashboard','lesson','vocabulary','grammar','skills','review','boss','knowledge','stats','profile'];

function routeFromPath(): AppRoute {
  const path = window.location.pathname;
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/pricing')) return 'pricing';
  if (path.startsWith('/login')) return 'login';
  if (path.startsWith('/telegram') || path.startsWith('/app')) {
    const view = new URLSearchParams(window.location.search).get('view') as AppRoute | null;
    if (view && appViews.includes(view)) return view;
    return 'dashboard';
  }
  return 'landing';
}

function useSystemTheme() {
  const [dark, setDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const onChange = () => setDark(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);
  return dark;
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AppState>(defaultState());
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [route, setRouteState] = useState<AppRoute>(routeFromPath());
  const saveTimer = useRef<number | undefined>(undefined);
  const licenseRefreshAt = useRef(0);
  const licenseRefreshPromise = useRef<Promise<PaidAccessRefreshResult> | null>(null);
  const licenseLastResult = useRef<PaidAccessRefreshResult | null>(null);
  const systemDark = useSystemTheme();

  const refreshEntitlement = useCallback(async (force = false): Promise<PaidAccessRefreshResult> => {
    const now = Date.now();
    const minAge = force ? 8_000 : 30_000;
    if (licenseLastResult.current && now - licenseRefreshAt.current < minAge) {
      return licenseLastResult.current;
    }
    if (licenseRefreshPromise.current) return licenseRefreshPromise.current;

    const task = (async () => {
      try {
        const license = await refreshLicenseHubStatus();
        const result: PaidAccessRefreshResult = {
          ok: true,
          active: Boolean(license.active && license.expires_at),
          reason: String(license.reason || (license.active ? 'ACTIVE' : 'UNKNOWN')),
          expiresAt: license.expires_at || undefined,
        };
        licenseRefreshAt.current = Date.now();
        licenseLastResult.current = result;

        setState((current) => {
          if (result.active && result.expiresAt) {
            if (current.profile.proUntil === result.expiresAt) return current;
            return { ...current, profile: { ...current.profile, proUntil: result.expiresAt } };
          }

          // Only account-level terminal answers may close PRO immediately. Device-specific
          // denials must not erase an otherwise valid mirrored account entitlement.
          const terminalReasons = new Set(['NO_LICENSE','EXPIRED','SUSPENDED','USER_DISABLED','REVOKED','REFUNDED']);
          if (terminalReasons.has(result.reason) && current.profile.proUntil) {
            return { ...current, profile: { ...current.profile, proUntil: undefined } };
          }
          return current;
        });

        return result;
      } catch (error) {
        return {
          ok: false,
          active: false,
          reason: error instanceof Error ? error.message : 'LICENSE_HUB_UNAVAILABLE',
        };
      } finally {
        licenseRefreshPromise.current = null;
      }
    })();

    licenseRefreshPromise.current = task;
    return task;
  }, []);

  useEffect(() => {
    (async () => {
      await initTelegram();
      const local = await loadState();
      let chosen = local;
      let serverProUntil: string | undefined;

      let session: Awaited<
        ReturnType<typeof getSession>
      > = null;

      try {
        session = await getSession();
      } catch {
        session = null;
      }

      const tg = telegramUser();

      let webTelegramSession =
        localStorage.getItem(
          'ef-telegram-session',
        );

      try {
        const cloud = await pullCloudState();
        serverProUntil = cloud?.profile.proUntil;

        if (
          webTelegramSession &&
          !session &&
          !tg &&
          !cloud
        ) {
          localStorage.removeItem(
            'ef-telegram-session',
          );

          webTelegramSession = null;
        }

        // Supabase yoki Telegram account mavjud bo'lsa
        // server state asosiy haqiqat hisoblanadi.
        //
        // Bu boshqa accountning eski localStorage profilini
        // yangi accountga ko'chib qolishidan himoya qiladi.
        if (session || tg || webTelegramSession) {
          chosen = cloud ?? defaultState();
        } else if (cloud?.version === local.version) {
          const cloudAt = cloud.lastSyncedAt
            ? new Date(cloud.lastSyncedAt).getTime()
            : 0;

          const localAt = local.lastSyncedAt
            ? new Date(local.lastSyncedAt).getTime()
            : 0;

          if (cloudAt > localAt) {
            chosen = cloud;
          }
        }
      } catch {
        // Offline holatda quyidagi auth gate
        // account bo'lmagan web userni app ichiga kiritmaydi.
      }

      // ======================================================
      // SUPABASE USER IDENTITY
      // ======================================================

      if (session?.user) {
        const metadata =
          session.user.user_metadata ?? {};

        const metadataName = String(
          metadata.full_name ??
          metadata.name ??
          [
            metadata.first_name,
            metadata.last_name,
          ]
            .filter(Boolean)
            .join(' ') ??
          ''
        ).trim();

        const fallbackName =
          session.user.email?.split('@')[0] ?? '';

        chosen = {
          ...chosen,
          profile: {
            ...chosen.profile,

            name:
              metadataName ||
              chosen.profile.name ||
              fallbackName,

            email:
              session.user.email ??
              chosen.profile.email,
          },
        };
      }

      // ======================================================
      // TELEGRAM IDENTITY
      // ======================================================

      if (tg) {
        const telegramName = [
          tg.first_name,
          tg.last_name,
        ]
          .filter(Boolean)
          .join(' ')
          .trim();

        chosen = {
          ...chosen,
          profile: {
            ...chosen.profile,

            name:
              chosen.profile.name ||
              telegramName,

            telegramId: String(tg.id),
          },
        };
      }

      // ======================================================
      // PAID ACCESS
      // ======================================================

      // Client/local state'dagi eski PRO muddatiga hech qachon ishonmaymiz.
      // Ammo pullCloudState() dan kelgan proUntil server mirror bo'lib, account-level
      // entitlement hisoblanadi. Device-specific DEVICE_NOT_ACTIVATED kabi javoblar
      // bu ishonchli mirrorni yo'q qilmasligi kerak.
      chosen = {
        ...chosen,
        profile: {
          ...chosen.profile,
          proUntil: serverProUntil,
        },
      };

      if (session || tg || webTelegramSession) {
        try {
          const license =
            await refreshLicenseHubStatus();

          licenseRefreshAt.current = Date.now();
          licenseLastResult.current = {
            ok: true,
            active: Boolean(license.active && license.expires_at),
            reason: String(license.reason || (license.active ? 'ACTIVE' : 'UNKNOWN')),
            expiresAt: license.expires_at || undefined,
          };

          if (
            license.active &&
            license.expires_at
          ) {
            chosen = {
              ...chosen,
              profile: {
                ...chosen.profile,
                proUntil: license.expires_at,
              },
            };
          } else {
            const terminalReasons = new Set([
              'NO_LICENSE',
              'EXPIRED',
              'SUSPENDED',
              'USER_DISABLED',
              'REVOKED',
              'REFUNDED',
            ]);
            if (terminalReasons.has(String(license.reason || ''))) {
              chosen = {
                ...chosen,
                profile: {
                  ...chosen.profile,
                  proUntil: undefined,
                },
              };
            }
          }
        } catch {
          // Faqat native Android kriptografik
          // tasdiqlangan offline receipt ishlatishi mumkin.
          const offline =
            await verifyCachedNativeLicense();

          if (
            offline.active &&
            offline.accessUntil
          ) {
            chosen = {
              ...chosen,
              profile: {
                ...chosen.profile,
                proUntil: offline.accessUntil,
              },
            };
          }
        }
      }

      setAuthenticated(Boolean(session || tg || webTelegramSession));
      setState(applyCalendarSnapshot(chosen));
      setReady(true);
    })();
  }, []);


  useEffect(() => {
    if (!ready) return;
    const refreshCalendar = () => setState((current) => applyCalendarSnapshot(current));
    refreshCalendar();
    const interval = window.setInterval(refreshCalendar, 60_000);
    document.addEventListener('visibilitychange', refreshCalendar);
    window.addEventListener('focus', refreshCalendar);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshCalendar);
      window.removeEventListener('focus', refreshCalendar);
    };
  }, [ready]);

  useEffect(() => {
    if (!ready || !authenticated) return;

    const refreshVisible = (force = false) => {
      if (document.visibilityState === 'hidden') return;
      void refreshEntitlement(force);
    };

    // Returning from SysOne checkout/admin activation must update PRO without a hard reload.
    refreshVisible(false);
    const interval = window.setInterval(() => refreshVisible(false), 60_000);
    const onFocus = () => refreshVisible(true);
    const onPageShow = () => refreshVisible(true);
    const onVisibility = () => refreshVisible(false);

    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [ready, authenticated, route, refreshEntitlement]);

  useEffect(() => {
    if (!ready) return;
    void ensureOfflinePack(state.progress.day);
  }, [ready, state.progress.day]);

  useEffect(() => {
    if (!ready) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      const withStamp = { ...state, lastSyncedAt: new Date().toISOString() };
      await saveState(withStamp);
      setState((current) => current.lastSyncedAt === state.lastSyncedAt ? withStamp : current);
      void pushCloudState(withStamp);
    }, 550);
    return () => window.clearTimeout(saveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.profile, state.progress, state.settings, ready]);

  const theme: 'light' | 'dark' = useMemo(() => {
    if (state.settings.theme === 'light') return 'light';
    if (state.settings.theme === 'dark') return 'dark';
    const tgTheme = telegramTheme();
    if (tgTheme) return tgTheme;
    return systemDark ? 'dark' : 'light';
  }, [state.settings.theme, systemDark]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0B0F17' : '#F5F6F8');
  }, [theme]);

  const setRoute = useCallback((next: AppRoute) => {
    setRouteState(next);
    const surface = window.location.pathname.startsWith('/telegram') ? '/telegram' : '/app';
    if (['landing','admin','pricing','login'].includes(next)) {
      const target = next === 'landing' ? '/' : `/${next}`;
      window.history.pushState({}, '', target);
    } else {
      window.history.pushState({}, '', `${surface}?view=${next}`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const onPop = () => setRouteState(routeFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const updateProfile = useCallback((patch: Partial<AppState['profile']>) => {
    setState((s) => ({ ...s, profile: { ...s.profile, ...patch } }));
  }, []);

  const updateSettings = useCallback((patch: Partial<AppState['settings']>) => {
    setState((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
  }, []);

  const answer = useCallback((word: WordItem, correct: boolean, latencyMs = 0, evidenceType: MasteryEvidenceType = 'meaning_recall') => {
    setState((s) => answerWord(s, word, correct, latencyMs, evidenceType));
    void queueLearningEvent({ type: 'vocabulary_attempt', skill: 'vocabulary', objectId: word.id, correct, latencyMs, evidenceType });
  }, []);

  const answerSkill = useCallback((skill: Exclude<Skill, 'vocabulary'>, correct: boolean, key = 'general') => {
    setState((s) => applySkillAnswer(s, skill, correct, key));
    void queueLearningEvent({ type: 'skill_attempt', skill, objectId: key, correct });
  }, []);

  const finishLesson = useCallback((score: number) => {
    setState((s) => {
      const next = completeLesson(s, score);
      void queueLearningEvent({
        type: 'lesson_complete',
        day: next.progress.day,
        courseDate: next.progress.courseDate,
        score,
        passed: score >= 75,
        reviewDebt: next.progress.reviewDebt,
      });
      return next;
    });
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode) => updateSettings({ theme: mode }), [updateSettings]);

  const syncNow = useCallback(async () => {
    const stamped = { ...state, lastSyncedAt: new Date().toISOString() };
    await saveState(stamped);
    const ok = await pushCloudState(stamped);
    if (!ok) return false;

    // Refresh License Hub first so a just-activated PRO subscription is mirrored before
    // sync-state is pulled back into the client.
    await refreshEntitlement(true);
    const cloud = await pullCloudState();
    if (cloud) setState(cloud);
    else setState((current) => ({ ...current, lastSyncedAt: stamped.lastSyncedAt }));
    return true;
  }, [state, refreshEntitlement]);

  const value = useMemo<AppContextValue>(() => ({
    state, ready, authenticated, route, setRoute, setState, updateProfile, updateSettings, answer, answerSkill, finishLesson,
    theme, setThemeMode, syncNow, refreshEntitlement, entitlement: getEntitlement(state),
  }), [state, ready, authenticated, route, setRoute, updateProfile, updateSettings, answer, answerSkill, finishLesson, theme, setThemeMode, syncNow, refreshEntitlement]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp must be used within AppProvider');
  return value;
}
